import React, { useState, useEffect, useRef } from "react";
import { COMPLIANT_LABEL, NON_COMPLIANT_LABEL, NA_LABEL, FIXED_COMPLIANT_LABEL, PARTIALLY_COMPLIANT_LABEL } from "src/constants/constants";
import { Column, Row, Text, Card, Divider, Expander, Icon, Button, Loader } from "@amzn/meridian";
import { InspectionResult, isOverridden, isFixedNow, isFixedLater, isVerified } from "src/services/inspectionService";
import { isResultNA } from "src/components/pages/inspection/utils";
import ThemedTag from "./ThemedTag";
import OverrideIcon from "./OverrideIcon";
import ComplianceResultRow from "./ComplianceResultRow";
import ComplianceDetailSheet from "./ComplianceDetailSheet";
import CountDetailSheet from "./CountDetailSheet";
import CheckpointVerifyFooter from "./CheckpointVerifyFooter";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import "./ReviewCheckpointCard.scss";

export interface ReviewCheckpointCardProps {
    questionNumber: string;
    questionText: string;
    isAI: boolean;
    isAutoCalculated?: boolean;
    allCompliant: boolean;
    isNA?: boolean;
    isAnalysisPending?: boolean;
    results: InspectionResult[];
    presignedUrlMap: Record<string, string>;
    onOverride?: (result: InspectionResult) => void;
    onFixNow?: (result: InspectionResult) => void;
    onFixLater?: (result: InspectionResult) => void;
    /** Verify / un-verify this checkpoint. Present only when it requires verification. */
    onVerify?: (verified: boolean) => void | Promise<void>;
    verifiedBy?: string | null;
    verifiedAt?: string | null;
    /** On return from a fix/override action, auto-expand the detailed list if still non-compliant. */
    autoExpand?: boolean;
}

const ReviewCheckpointCard: React.FC<ReviewCheckpointCardProps> = ({
    questionNumber,
    questionText,
    isAI,
    isAutoCalculated = false,
    results,
    allCompliant,
    isNA = false,
    isAnalysisPending = false,
    presignedUrlMap,
    onOverride,
    onFixNow,
    onFixLater,
    onVerify,
    verifiedBy,
    verifiedAt,
    autoExpand = false,
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [selectedResult, setSelectedResult] = useState<InspectionResult | null>(null);
    const [sheetOpen, setSheetOpen] = useState(false);

    const isFixCompliant = allCompliant && results.some(isFixedNow);

    // At least one row deferred (fix-it-later) and every other scored row is
    // compliant (on its own or via fix-it-now). N/A rows are excluded, matching
    // how allCompliant / the overall label are computed. A non-compliant row that
    // is not deferred makes the checkpoint Non Compliant instead.
    const scoredResults = results.filter((r) => !isResultNA(r));
    const isFixLaterPartiallyCompliant =
        scoredResults.some(isFixedLater) &&
        scoredResults.every((r) => r.isCompliant || isFixedLater(r));

    // Mirrors the red "Non Compliant" badge state below: an unresolved
    // non-compliant leaf remains (not N/A, not all compliant, not merely deferred).
    const isNonCompliant = !isNA && !allCompliant && !isFixLaterPartiallyCompliant;

    // On return from a fix/override action (autoExpand), open the inline detailed
    // list once if the checkpoint is still non-compliant, so the inspector doesn't
    // have to tap to see what's left. A useRef one-shot guard keeps the ~3s review
    // poll (which re-renders this card) from re-opening it after a manual collapse.
    const autoExpandedRef = useRef(false);
    useEffect(() => {
        if (!autoExpand) {
            autoExpandedRef.current = false;
            return;
        }
        if (autoExpandedRef.current) return;
        if (isNonCompliant) {
            autoExpandedRef.current = true;
            setShowDetails(true);
        }
    }, [autoExpand, isNonCompliant]);

    // Verification is question-level; every result under it carries the same stamp,
    // so the first is representative.
    const verified = results.length > 0 && isVerified(results[0]);
    // The verify checkbox is interactive once the checkpoint is settled and there's
    // nothing left to resolve: N/A (nothing to assess), compliant, or deferred via
    // fix-it-later. A still-non-compliant checkpoint can't be verified yet.
    const verifyEnabled =
        !isAnalysisPending && (isNA || allCompliant || isFixLaterPartiallyCompliant);

    const openSheet = (result: InspectionResult) => {
        setSelectedResult(result);
        setSheetOpen(true);
    };

    const handleOverride = (result: InspectionResult) => {
        setSheetOpen(false);
        onOverride?.(result);
    };

    const handleFixNow = (result: InspectionResult) => {
        setSheetOpen(false);
        onFixNow?.(result);
    };

    const handleFixLater = (result: InspectionResult) => {
        setSheetOpen(false);
        onFixLater?.(result);
    };

    return (
        <Card spacingInset="none">
            <Column spacingInset="400" spacing="300">
                <Row alignmentHorizontal="justify" alignmentVertical="center">
                    <Text type="h100" color="primary">
                        {questionNumber}
                    </Text>
                    <Row spacing="200" alignmentVertical="center">
                        {isAnalysisPending ? (
                            <Row spacing="200" alignmentVertical="center">
                                <Text type="b100" color="secondary">Analyzing...</Text>
                                <Loader size="small" type="circular" />
                            </Row>
                        ) : isNA ? (
                            <ThemedTag text={NA_LABEL} type="gray" />
                        ) : isFixCompliant ? (
                            <ThemedTag text={FIXED_COMPLIANT_LABEL} type="fixCompliant" />
                        ) : allCompliant ? (
                            <ThemedTag text={COMPLIANT_LABEL} type="success" />
                        ) : isFixLaterPartiallyCompliant ? (
                            <ThemedTag text={PARTIALLY_COMPLIANT_LABEL} type="fixLater" />
                        ) : (
                            <ThemedTag text={NON_COMPLIANT_LABEL} type="error" />
                        )}

                        {/* Show AI/Manual/Auto source tag only after analysis is complete */}
                        {!isAnalysisPending && <ThemedTag text={isAutoCalculated ? "Auto" : isAI ? "AI" : "Manual"} type="neutral" />}
                    </Row>
                </Row>

                <Text type="b200" color="secondary">
                    {questionText}
                </Text>

                <Divider />

                {!results[0].objectId ?
                    <Column spacing="200">
                        <Row alignmentHorizontal="justify" alignmentVertical="center" spacingInset="200 none">
                            <Column spacing="200">
                                <Text type="b200" color="secondary">Answer: {results[0].confirmedResult}</Text>
                                {isOverridden(results[0]) && (<Row>
                                    <ThemedTag text="Manually Overridden" type="override" icon={<OverrideIcon />} />
                                </Row>)}
                            </Column>
                            <Button onClick={() => openSheet(results[0])} type="icon">
                                <Icon tokens={chevronRightSmallTokens} color="secondary" />
                            </Button>
                        </Row>
                    </Column> :

                    <Expander
                        title="Detailed compliance status"
                        open={showDetails}
                        onChange={(open: boolean) => setShowDetails(open)}
                        type="inline"
                        spacingInset="none"
                    >
                        <Column spacing="none">
                            {results.map((result, idx) => (
                                <ComplianceResultRow
                                    key={result.resultId}
                                    result={result}
                                    index={idx}
                                    onClick={() => openSheet(result)}
                                    isLast={idx === results.length - 1}
                                />
                            ))}
                        </Column>
                    </Expander>}
            </Column>

            {onVerify && !isAnalysisPending && (
                <CheckpointVerifyFooter
                    verified={verified}
                    verifiedBy={verifiedBy}
                    verifiedAt={verifiedAt}
                    enabled={verifyEnabled}
                    onVerify={onVerify}
                />
            )}

            {isAutoCalculated ? (
                <CountDetailSheet
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    result={selectedResult}
                    questionText={questionText}
                    onOverride={onOverride ? handleOverride : undefined}
                />
            ) : (
                <ComplianceDetailSheet
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    result={selectedResult}
                    questionText={questionText}
                    isAI={isAI}
                    isAutoCalculated={isAutoCalculated}
                    presignedUrlMap={presignedUrlMap}
                    onOverride={onOverride ? handleOverride : undefined}
                    onFixNow={onFixNow ? handleFixNow : undefined}
                    onFixLater={onFixLater ? handleFixLater : undefined}
                />
            )}
        </Card>
    );
};

export default ReviewCheckpointCard;
